import React, { useContext, useEffect, useState } from 'react';
import { Box, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { ListingCalendarType } from '../../../types/db';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { isValidCalendar, useCalendarOnBlurCallback, useGuestCalendarOnChangeCallback, useCalendarTypeOnChangeCallback, isSameCalendar, useCohostDiffCalendarCheckOnChangeCallback, useCohostCalendarOnChangeCallback } from '../../../utilities/listings';
import { ListingEditContext } from '../../../context/Listing/ListingEditFlow';
import { ListingEditSectionView } from './ListingEditSectionView';
import { CalendarInput, CalendarInputFieldName, ICalendarInputProps } from '../../../components/listing/CalendarInput';
import { CalendarDisplay } from '../../../components/listing/CalendarDisplay';
import { IssueStateView } from '../../../components/IssueStateView';

/** The calendar page of the listing creation flow */
export const ListingCalendarPage = () => {
    return <Container />
}

type RadioChoice = ListingCalendarType;

const Container = () => {
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext')
    }

    const { listing, isLoading } = listingEditContext;

    if (listing?.pricing?.type === 'non-bookable') {
        return (
            <Box>
                <Typography>{t('listing.calendar.nonBookableMessage')}</Typography>
            </Box>
        );
    }

    if (isLoading) {
        return <CircularProgress />
    }

    return (
        <Stack direction={'column'} spacing={2}>
            <ListingCalendarAvailability />
            <Divider />
            <ListingGuestCalendar />
            <Divider />
            <ListingCohostCalendar />
        </Stack>
    );
}

const ListingCalendarAvailability = () => {
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext')
    }

    const { listing, setActivePageInfo, handleCancel, handleSave, completed } = listingEditContext;

    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (!editing) return;// no need to check if not editing
        if (listing?.pricing?.type === 'non-bookable') {
            //if the listing is non-bookable, let the user skip this step (should never go there since blocked higher)
            setActivePageInfo({}, true);
        } else if (isValidCalendar(listing?.calendar)) {
            //only check the guest/general calendar
            setActivePageInfo({}, true)
        } else {
            //reset completion
            setActivePageInfo({}, false)
        }
    }, [listing?.calendar, editing, listing?.pricing?.type, setActivePageInfo]);


    // The type of calendar
    const calendarType: RadioChoice | undefined = listing?.calendar?.type;
    const handleCalendarTypeChange = useCalendarTypeOnChangeCallback(setActivePageInfo);

    return <ListingEditSectionView title={t('listing.edit.typeCalendarTitle')}
        isEditMode={editing}
        onEditClick={() => setEditing(true)}
        onCancelClick={() => { handleCancel(); setEditing(false) }}
        onSaveClick={() => {
            if (!completed) return;
            handleSave().then(() => setEditing(false))
        }}
        isSaveDisabled={!completed}>
        {
            !editing ?
                <Typography>{t('listing.calendar.type', { context: calendarType === 'default-available' ? 'all' : 'none' })}</Typography>
                :
                <FormControl>
                    <RadioGroup
                        value={calendarType || ''}
                        onChange={(ev) => handleCalendarTypeChange(ev.target.value as RadioChoice)}
                    >
                        <FormControlLabel
                            value={"default-available" as RadioChoice}
                            control={<Radio />}
                            label={t('listing.calendar.type', { context: 'all' })}
                        />
                        <FormControlLabel
                            value={"default-non-available" as RadioChoice}
                            control={<Radio />}
                            label={t('listing.calendar.type', { context: 'none' })}
                        />
                    </RadioGroup>
                </FormControl>
        }
    </ListingEditSectionView>
}

export const ListingGuestCalendar = () => {
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext')
    }

    const { listing, setActivePageInfo, handleCancel, handleSave, completed } = listingEditContext;

    const { t } = useTranslation();

    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (!editing) return;// no need to check if not editing
        if (listing?.pricing?.type === 'non-bookable') {
            //if the listing is non-bookable, let the user skip this step
            setActivePageInfo({}, true);
        } else if (isValidCalendar(listing?.calendar)) {
            //only check the guest/general calendar
            setActivePageInfo({}, true)
        } else {
            //reset completion
            setActivePageInfo({}, false)
        }
    }, [listing?.calendar, editing, listing?.pricing?.type, setActivePageInfo]);


    // The guest calendar
    const [guestCalendarErrors, setGuestCalendarErrors] = useState<{ [key in CalendarInputFieldName]?: string }>({});
    const guestCalendar: ICalendarInputProps = {
        minNight: {
            value: listing?.calendar?.min_night?.toString() || '',
            helperText: guestCalendarErrors['minNight'],
            error: !!guestCalendarErrors['minNight'],
        },
        maxNight: {
            value: listing?.calendar?.max_night?.toString() || '',
            helperText: guestCalendarErrors['maxNight'],
            error: !!guestCalendarErrors['maxNight'],
        },
    }
    const handleGuestCalendarChange = useGuestCalendarOnChangeCallback(setActivePageInfo, setGuestCalendarErrors);

    const handleCalendarOnBlur = useCalendarOnBlurCallback(t, setGuestCalendarErrors);

    return <ListingEditSectionView title={t('listing.edit.guestCalendarTitle')}
        isEditMode={editing}
        onEditClick={() => setEditing(true)}
        onCancelClick={() => { handleCancel(); setEditing(false) }}
        onSaveClick={() => {
            if (!completed) return;
            handleSave().then(() => setEditing(false))
        }}
        isSaveDisabled={!completed}>
        {
            !editing ?
                <CalendarDisplay
                    minNight={listing?.calendar?.min_night || 0}
                    maxNight={listing?.calendar?.max_night}
                />
                :
                <CalendarInput {...{
                    ...guestCalendar,
                    minNight: {
                        ...guestCalendar.minNight,
                        onChange: (ev) => handleGuestCalendarChange('minNight', ev.target.value),
                        onBlur: (ev) => handleCalendarOnBlur(listing?.calendar, 'minNight', ev.target.value),
                    },
                    maxNight: {
                        ...guestCalendar.maxNight,
                        onChange: (ev) => handleGuestCalendarChange('maxNight', ev.target.value),
                        onBlur: (ev) => handleCalendarOnBlur(listing?.calendar, 'maxNight', ev.target.value),
                    },
                }} />
        }
    </ListingEditSectionView>
}

export const ListingCohostCalendar = ({ title }: { title?: string }) => {
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext')
    }

    const { listing, setActivePageInfo, handleCancel, handleSave, completed } = listingEditContext;

    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (!editing) return;// no need to check if not editing
        if (listing?.pricing?.type === 'non-bookable') {
            //if the listing is non-bookable, let the user skip this step
            setActivePageInfo({}, true);
        } else if (!listing?.cohost_perk?.open
            && isValidCalendar(listing?.calendar)) {
            //if there isn't cohost perk, only check the guest/general calendar
            setActivePageInfo({}, true)
        } else if (!!listing?.cohost_perk?.calendar
            && isValidCalendar(listing?.cohost_perk.calendar, true)
            && isValidCalendar(listing?.calendar)) {
            //if there cohost perk with a calendar, and its calendar is valid as well as the guest/general calendar
            setActivePageInfo({}, true)
        } else {
            //reset completion
            setActivePageInfo({}, false)
        }
    }, [listing?.calendar, listing?.cohost_perk, editing, listing?.pricing?.type, setActivePageInfo]);

    // The cohost calendar
    const isListingOpenToCohost = !!listing?.cohost_perk?.open;
    const isDiffCohostSettings = !!listing?.calendar && !!listing?.cohost_perk?.calendar && (!isSameCalendar(listing?.calendar, listing?.cohost_perk?.calendar) || Object.keys(listing?.cohost_perk.calendar || {}).length === 0);
    const [diffChecked, setDiffChecked] = useState(isDiffCohostSettings);
    const handleDiffCohostCheckOnChange = useCohostDiffCalendarCheckOnChangeCallback(setActivePageInfo);

    const [cohostCalendarErrors, setCohostCalendarErrors] = useState<{ [key in CalendarInputFieldName]?: string }>({});
    const cohostCalendar: ICalendarInputProps = {
        minNight: {
            value: listing?.cohost_perk?.calendar?.min_night?.toString() || '',
            helperText: cohostCalendarErrors['minNight'],
            error: !!cohostCalendarErrors['minNight'],
        },
        maxNight: {
            value: listing?.cohost_perk?.calendar?.max_night?.toString() || '',
            helperText: cohostCalendarErrors['maxNight'],
            error: !!cohostCalendarErrors['maxNight'],
        }
    };
    const handleCohostCalendarChange = useCohostCalendarOnChangeCallback(setActivePageInfo);
    const handleCohostCalendarOnBlur = useCalendarOnBlurCallback(t, setCohostCalendarErrors)

    // if (!isListingOpenToCohost) {
    //     return null;
    // }
    return <ListingEditSectionView title={title || t('listing.edit.cohostCalendarTitle')}
        isEditMode={editing}
        onEditClick={isListingOpenToCohost ? (() => setEditing(true)) : undefined}
        onCancelClick={() => { handleCancel(); setEditing(false) }}
        onSaveClick={() => {
            if (!completed) return;
            handleSave().then(() => setEditing(false))
        }}
        isSaveDisabled={!completed}>
        {
            !isListingOpenToCohost ?
                <IssueStateView title={t('listing.calendar.notOpenToCohostTitle')}
                    description={t('listing.calendar.notOpenToCohostDescription')} />
                :
                !editing ?
                    isDiffCohostSettings ?
                        <CalendarDisplay
                            minNight={listing.cohost_perk?.calendar?.min_night}
                            maxNight={listing.cohost_perk?.calendar?.max_night} />
                        :
                        <Typography>{t('listing.edit.cohostCalendarDisabled')}</Typography>
                    :
                    <FormControl>
                        <FormControlLabel control={
                            <Checkbox
                                {...{
                                    checked: diffChecked || isDiffCohostSettings,
                                    onChange: (_, checked) => { handleDiffCohostCheckOnChange(checked); setDiffChecked(checked) },
                                }} />}
                            label={t('listing.calendar.cohostDiffLabel')} />
                        {
                            (diffChecked || isDiffCohostSettings) &&
                            <CalendarInput {...{
                                ...cohostCalendar,
                                minNight: {
                                    ...cohostCalendar.minNight,
                                    onChange: (ev) => handleCohostCalendarChange('minNight', ev.target.value),
                                    onBlur: (ev) => handleCohostCalendarOnBlur(listing?.cohost_perk?.calendar, 'minNight', ev.target.value),
                                },
                                maxNight: {
                                    ...cohostCalendar.maxNight,
                                    onChange: (ev) => handleCohostCalendarChange('maxNight', ev.target.value),
                                    onBlur: (ev) => handleCohostCalendarOnBlur(listing?.cohost_perk?.calendar, 'maxNight', ev.target.value),
                                },
                            }} />
                        }
                    </FormControl>
        }
    </ListingEditSectionView>
}
